(() => {
  if (window.__aboutPageInit) return;
  window.__aboutPageInit = true;

  const SELECTOR = '[data-about-reveal]';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    (document.documentElement.classList.contains('disable-animations') &&
      window.matchMedia('(max-width: 749px)').matches);

  const revealAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => el.classList.add('is-inview'));
  };

  const observe = (root = document) => {
    const nodes = Array.from(root.querySelectorAll(`${SELECTOR}:not(.is-inview)`));
    if (!nodes.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );

    nodes.forEach((node) => observer.observe(node));
  };

  const names = new Set();
  let ticking = false;

  const measureNames = () => {
    ticking = false;
    if (prefersReducedMotion()) return;

    const vh = window.innerHeight || 1;
    names.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const total = rect.height + vh;
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / total));
      section.style.setProperty('--about-name-progress', progress.toFixed(4));
    });
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measureNames);
  };

  const collectNames = (root = document) => {
    root.querySelectorAll('[data-about-name]').forEach((section) => names.add(section));
    onScroll();
  };

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const moreBlocks = new Set();

  const lineHeightOf = (el) => {
    const sample = el.querySelector('p, li, h2, h3, h4') || el;
    const value = parseFloat(window.getComputedStyle(sample).lineHeight);
    return Number.isFinite(value) && value > 0 ? value : 24;
  };

  const collapseBlock = (block, instant = false) => {
    const rte = block.querySelector('[data-about-more-text]');
    const button = block.querySelector('[data-about-more-button]');
    const label = button?.querySelector('[data-about-more-label]');
    if (!rte) return;

    const lines = parseInt(block.dataset.aboutMoreLines, 10) || 5;
    const maxHeight = lineHeightOf(rte) * lines;
    const reduce = prefersReducedMotion() || instant;

    if (reduce) {
      rte.style.transition = 'none';
      rte.classList.remove('is-expanded');
      rte.classList.add('is-clamped');
      rte.style.maxHeight = `${maxHeight}px`;
    } else {
      rte.style.maxHeight = `${rte.scrollHeight}px`;
      rte.style.transition = 'none';
      void rte.offsetHeight;
      rte.style.transition = 'max-height 550ms cubic-bezier(0.16, 1, 0.3, 1)';
      rte.classList.remove('is-expanded');
      rte.classList.add('is-clamped');
      rte.style.maxHeight = `${maxHeight}px`;
    }

    if (button) {
      button.hidden = false;
      button.setAttribute('aria-expanded', 'false');
      if (label) label.textContent = block.dataset.aboutMoreOpen || '';
    }
  };

  const expandBlock = (block, instant = false) => {
    const rte = block.querySelector('[data-about-more-text]');
    const button = block.querySelector('[data-about-more-button]');
    const label = button?.querySelector('[data-about-more-label]');
    if (!rte) return;

    const reduce = prefersReducedMotion() || instant;
    rte.classList.remove('is-clamped');
    rte.classList.add('is-expanded');

    if (reduce) {
      rte.style.transition = 'none';
      rte.style.maxHeight = 'none';
    } else {
      rte.style.transition = 'max-height 550ms cubic-bezier(0.16, 1, 0.3, 1)';
      rte.style.maxHeight = `${rte.scrollHeight}px`;
      window.setTimeout(() => {
        if (rte.classList.contains('is-expanded')) rte.style.maxHeight = 'none';
      }, 560);
    }

    if (button) {
      button.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = block.dataset.aboutMoreClose || '';
    }
  };

  const resetBlock = (block) => {
    const rte = block.querySelector('[data-about-more-text]');
    const button = block.querySelector('[data-about-more-button]');
    if (!rte) return;
    rte.classList.remove('is-clamped', 'is-expanded');
    rte.style.maxHeight = '';
    rte.style.transition = 'none';
    if (button) {
      button.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    }
  };

  const measureBlock = (block) => {
    const rte = block.querySelector('[data-about-more-text]');
    const button = block.querySelector('[data-about-more-button]');
    if (!rte || !button) return;

    const mobileOnly = block.dataset.aboutMoreMobile !== 'false';
    if (mobileOnly && !mobileQuery.matches) {
      resetBlock(block);
      return;
    }

    const wasExpanded = rte.classList.contains('is-expanded');
    rte.classList.remove('is-clamped');
    rte.style.maxHeight = 'none';

    const lines = parseInt(block.dataset.aboutMoreLines, 10) || 5;
    const maxHeight = lineHeightOf(rte) * lines;
    const overflows = rte.scrollHeight > maxHeight + 8;

    if (!overflows) {
      resetBlock(block);
      return;
    }

    if (wasExpanded) expandBlock(block, true);
    else collapseBlock(block, true);
  };

  const bindBlock = (block) => {
    if (block.dataset.aboutMoreReady === 'true') return;
    block.dataset.aboutMoreReady = 'true';

    const button = block.querySelector('[data-about-more-button]');
    button?.addEventListener('click', () => {
      const rte = block.querySelector('[data-about-more-text]');
      if (!rte) return;
      if (rte.classList.contains('is-expanded')) collapseBlock(block);
      else expandBlock(block);
    });
  };

  const initReadMore = (root = document) => {
    root.querySelectorAll('[data-about-more]').forEach((block) => {
      moreBlocks.add(block);
      bindBlock(block);
      measureBlock(block);
    });
  };

  const refreshReadMore = () => {
    moreBlocks.forEach((block) => {
      if (!block.isConnected) {
        moreBlocks.delete(block);
        return;
      }
      measureBlock(block);
    });
  };

  const init = (root) => {
    observe(root || document);
    collectNames(root || document);
    initReadMore(root || document);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('resize', refreshReadMore, { passive: true });
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', refreshReadMore);
  } else {
    mobileQuery.addListener(refreshReadMore);
  }
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => collectNames());
})();
